import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNotifSettings, useUpdateNotifSettings } from '../../hooks/useMasters.js';
import Card from '../../components/ui/Card.jsx';
import Button from '../../components/ui/Button.jsx';
import Input from '../../components/ui/Input.jsx';

const schema = z.object({
  smtpHost: z.string().optional(),
  smtpPort: z.coerce.number().optional(),
  smtpUser: z.string().optional(),
  smtpPass: z.string().optional(),
  fromEmail: z.string().email().optional().or(z.literal('')),
  dnGraceBufferMinutes: z.coerce.number().min(0),
  returnableReminderDays: z.coerce.number().min(0),
});

export default function NotificationSettingsPage() {
  const { data: settings, isLoading } = useNotifSettings();
  const update = useUpdateNotifSettings();
  const { register, handleSubmit, reset, formState: { errors, isSubmitting, isDirty } } = useForm({ resolver: zodResolver(schema) });

  useEffect(() => { if (settings) reset(settings); }, [settings, reset]);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Notification Settings</h2>
        <p className="text-sm text-gray-500">SMTP configuration and reminder timings</p>
      </div>
      {isLoading ? <div className="p-8 text-center text-gray-400">Loading…</div> : (
        <form onSubmit={handleSubmit(values => update.mutateAsync(values))} className="space-y-6">
          <Card title="SMTP Configuration">
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Input label="SMTP Host" placeholder="smtp.example.com" {...register('smtpHost')} />
                <Input label="SMTP Port" type="number" placeholder="587" {...register('smtpPort')} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Input label="SMTP User" type="email" {...register('smtpUser')} />
                <Input label="SMTP Password" type="password" placeholder={settings?.smtpPassSet ? '••••••••' : 'Not set'} {...register('smtpPass')} />
              </div>
              <Input label="From Email" type="email" placeholder="no-reply@sreeragamexports.com" error={errors.fromEmail?.message} {...register('fromEmail')} />
            </div>
          </Card>
          <Card title="Reminder Timings">
            <div className="p-6 grid grid-cols-2 gap-4">
              <Input label="Delivery Note grace buffer (minutes)" type="number" hint="Extra time before overdue reminders start" {...register('dnGraceBufferMinutes')} />
              <Input label="Returnable reminder lead time (days)" type="number" hint="Days before expected return to send reminder" {...register('returnableReminderDays')} />
            </div>
          </Card>
          <div className="flex justify-end">
            <Button type="submit" loading={isSubmitting} disabled={!isDirty}>Save Settings</Button>
          </div>
        </form>
      )}
    </div>
  );
}
